import React, {useEffect, useState, useMemo, useCallback} from 'react';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import 'quill-emoji/dist/quill-emoji.css';
import 'quill-emoji';
import * as css from './TextEditor.css.js';
import {SnowTheme} from 'quill-color-picker-enhance';
import 'quill-color-picker-enhance/dist/index.css';
import {addNoDelete, addVariables, convertVariablesToHtml} from './Variables.js';
import {useVariables} from '../../assets/objects/Variables.js';
import VariableDropdown from './VariablesDropdown.js';
import {useTranslation} from 'react-i18next';
import DOMPurify from 'dompurify';
import {SMS_CHARS_ON_MESSAGE, SMS_FREE_CHARS} from '../../pages/branding/smsEditor/SmsEditor.js';
import {DEFAULT_LANG} from '../../globalVariables/locals.js';
import {decodeHtmlEntities,clearText,convertTextBeforeSave,hasDisabledVariables} from './TextEditorHelpers.js';
import {handlePaste as handlePasteUtil,handleCopy as handleCopyUtil,createImageHandler} from './TextEditorHandlers.js';
import {getModulesConfig} from './TextEditorModules.js';

function TextEditor({defaultText, onSave, onChange, showToolbar = false,limit={}, quillRef, dependencies = [], lang=DEFAULT_LANG, prefix=null, suffix=null, variablesType='', expanded = false}) {
    const Quill = ReactQuill.Quill;
    Quill.register('themes/snow-quill-color-picker-enhance', SnowTheme);
    const {t} = useTranslation('variables');
    const [text, setText] = useState('');
    const {defaultVariables, disabledVariables} = useVariables(variablesType);
    const [showWarning, setShowWarning] = useState(false);
    const variables = {...defaultVariables, ...disabledVariables};
    const {showCharCounter,maxChar,showMessagesAmount}=limit;
    const direction=lang!=='he'?'ltr':'rtl';
    
    const saveText = () => {
        if(quillRef.current) {
            onSave(convertTextBeforeSave(quillRef));
        }
    };

    useEffect(() => {
        if(quillRef.current ) {
            addVariables();
            addNoDelete();
            const quill = quillRef.current.getEditor();
            let htmlWithValues = '';  
            if(defaultText) {
                htmlWithValues = convertVariablesToHtml(defaultText, variables, t);
                htmlWithValues = htmlWithValues.replace(/<p><\/p>/g, '<p><br></p>');
            }
            quill.setContents(quill.clipboard.convert(DOMPurify.sanitize(htmlWithValues)));
        }
    }, dependencies);

    const imageHandler = useCallback(createImageHandler(quillRef), [quillRef]);

    const handlePaste = useCallback((e) => {
        handlePasteUtil(e, quillRef, maxChar);
    }, [quillRef, maxChar]);

    const handleCopy = useCallback((e) => {
        handleCopyUtil(e, quillRef);
    }, [quillRef]);

    useEffect(() => {
        if(quillRef?.current) {
            const quill = quillRef?.current?.getEditor();
            const checkForDisabledVariables = () => {
                const disabled = hasDisabledVariables(quillRef, disabledVariables);
                setShowWarning(disabled);
            };
            // fix bug in color-picker position
            const toolbarElement = quill.getModule('toolbar').container;
            const createAndAppendInput = (className) => {
                const input = document.createElement('input');
                input.type = 'color';
                input.hidden = true;
                input.className = className;
                toolbarElement.appendChild(input);
            };
            createAndAppendInput('custom-picker color');
            createAndAppendInput('custom-picker background');

            checkForDisabledVariables();
            quill?.on('text-change', checkForDisabledVariables);
            quill?.on('blur', saveText);
            quill.root.addEventListener('blur', saveText);
            quill.root.addEventListener('paste', handlePaste);
            quill.root.addEventListener('copy', handleCopy);
            return () => {
                quill.off('text-change', checkForDisabledVariables);
                quill.off('blur', saveText);
                quill.root.removeEventListener('blur', saveText);
                quill.root.removeEventListener('paste', handlePaste);
                quill.root.removeEventListener('copy', handleCopy);
            };
        }
    }, [handlePaste, handleCopy, saveText, disabledVariables]);

    const modules = useMemo(() => getModulesConfig(imageHandler, expanded), [imageHandler, expanded]);

    const handleTextChange = (value) => {
        const clearValue = clearText(value);
        if(onChange && value){     
            onChange(decodeHtmlEntities(convertTextBeforeSave(quillRef)));
        }
        setText(clearValue);
    };

    const isMaxLengthReached = maxChar ? text.length >= maxChar : false;

    const handleKeyDown = (event) => {
        if(isMaxLengthReached && event.key !== 'Backspace') {
            event.preventDefault();
        }
    };

    return (
        <css.wrap direction={direction} showToolbar={showToolbar} showCharCounter={showCharCounter}>
            <VariableDropdown direction={direction} quillRef={quillRef} remainingChars={maxChar-text.length} variablesType={variablesType}/>
            {!!prefix && prefix}
            <ReactQuill
                onChange={handleTextChange}
                onKeyDown={handleKeyDown}
                ref={quillRef}
                modules={modules}
                theme='snow-quill-color-picker-enhance'
            />
            {!!suffix && suffix}
            {showCharCounter&&<css.maxChar>
                {`${text.length}/${maxChar} `}
                <b>{t('chars')}   </b>
                {(showMessagesAmount&& text.length>SMS_FREE_CHARS)&&
                <>{`${t('messagesCount')}:${(Math.ceil((text.length-SMS_FREE_CHARS) / SMS_CHARS_ON_MESSAGE))} `}
                    <span>{t('additionalCost')}</span>
                </>}
            </css.maxChar>}
            {showWarning && <css.warning>{t('warningDisabled')}</css.warning>}
        </css.wrap>
    );
}

export default TextEditor;
